import { useEffect, useRef } from 'react';
import { useBroadcastSetup } from 'utils/broadcast-setup';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { useAppSelector } from 'store/hooks';
import { selectDeviceInfo } from 'features/application/applicationSlice';

/**
 * Custom hook that sets up keyboard shortcuts for toggling audio and video on the join screen.
 * - Audio: Shift + Cmd/Alt + A
 * - Video: Shift + Cmd/Alt + V
 */
export const useJoinMediaKeyboardShortcuts = () => {
  const { hasPermissions } = usePermissions();
  const { audio, video, onAudioToggle, onVideoToggle } = useBroadcastSetup();
  const deviceInfo = useAppSelector(selectDeviceInfo);
  const isMacOS = deviceInfo.os.name === 'Mac OS';

  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);
  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);

  // Store only frequently changing state values in refs
  const audioStateRef = useRef({
    active: audio.active,
    disabled: audio.disabled,
    processing: audio.processing,
  });

  const videoStateRef = useRef({
    active: video.active,
    disabled: video.disabled,
    processing: video.processing,
  });

  audioStateRef.current = {
    active: audio.active,
    disabled: audio.disabled,
    processing: audio.processing,
  };

  videoStateRef.current = {
    active: video.active,
    disabled: video.disabled,
    processing: video.processing,
  };

  useEffect(() => {
    if (!allowAudioBroadcast) {
      return;
    }

    const handleKeydown = (event: KeyboardEvent) => {
      const audioState = audioStateRef.current;
      if (audioState.disabled || audioState.processing) {
        return;
      }

      const key = event.key?.toLowerCase();
      const isMacCombo = isMacOS && event.metaKey && event.shiftKey;
      const isWindowsCombo = !isMacOS && event.altKey && event.shiftKey;

      if ((isMacCombo || isWindowsCombo) && key === 'a') {
        onAudioToggle(!audioState.active);
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [allowAudioBroadcast, onAudioToggle, isMacOS]);

  useEffect(() => {
    if (!allowVideoBroadcast) {
      return;
    }

    const handleKeydown = (event: KeyboardEvent) => {
      const videoState = videoStateRef.current;
      if (videoState.disabled || videoState.processing) {
        return;
      }

      const key = event.key?.toLowerCase();
      const isMacCombo = isMacOS && event.metaKey && event.shiftKey;
      const isWindowsCombo = !isMacOS && event.altKey && event.shiftKey;

      if ((isMacCombo || isWindowsCombo) && key === 'v') {
        onVideoToggle(!videoState.active);
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [allowVideoBroadcast, onVideoToggle, isMacOS]);
};
